// AllCloudCostAnomalies.jsx
import React, { useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceDot,
  Line,
  LineChart,
} from "recharts";
import styles from "@/page-components/Cost-Anomaly/anomaly.module.css";
import awsLogo from "@/assets/img/aws_logo.svg";
import azureLogo from "@/assets/img/azurelogo.svg";
import gcpLogo from "@/assets/img/gcp_logo.svg";
import idleStyles from "@/page-components/idle-resource/idle.module.css";
import { useBarGraphAnomalies } from "@/services/cost-anomaly/get-all-cloud-bar-graph.repo";
import config from "@/config";
import { compareAsc, format, parse, parseISO } from "date-fns";
import ScenariosErrorState from "@/components/States/scenariosErrorState";
import BarGraphErrorStates from "@/components/States/BarGraphErrorStates";
import Dropdown from "react-bootstrap/Dropdown";
import { CSVLink } from "react-csv";
import menuicon from "@/assets/img/menu.svg";
import download from "@/assets/img/download.svg";
import barcharticon from "@/assets/img/graphCharticon.svg";
import linecharticon from "@/assets/img/lineCharticon.svg";

const AllCloudStackedBarGraph = ({ service }) => {
  return (
    <div style={{ width: "100%", padding: "24px 40px 0px 40px" }}>
      <StackedCloudAnomaliesChart service={service} />
    </div>
  );
};
export default AllCloudStackedBarGraph;

export const StackedCloudAnomaliesChart = ({ service }) => {
  const [chartType, setChartType] = useState("bar");
  const csvLinkRef = useRef(null);

  const query = useMemo(() => {
    return {
      service,
    };
  }, [service]);

  const { data, isLoading, error } = useBarGraphAnomalies(query);

  const colorMap = {
    AWS: "#F90",
    Azure: "#1F78B4",
    GCP: "#0F9D58",
  };
  const datelyData = {};

  if (config.enabledClouds.has("aws") && data?.aws?.length > 0) {
    data?.aws?.forEach(({ date, anomalies }) => {
      if (!datelyData[date]) {
        datelyData[date] = { AWS: 0, Azure: 0, GCP: 0 };
      }
      datelyData[date].AWS = anomalies;
    });
  }

  if (config.enabledClouds.has("azure") && data?.azure?.length > 0) {
    data?.azure?.forEach(({ date, anomalies }) => {
      if (!datelyData[date]) {
        datelyData[date] = { AWS: 0, Azure: 0, GCP: 0 };
      }
      datelyData[date].Azure = anomalies;
    });
  }

  if (config.enabledClouds.has("gcp") && data?.gcp?.length > 0) {
    data?.gcp?.forEach(({ date, anomalies }) => {
      if (!datelyData[date]) {
        datelyData[date] = { AWS: 0, Azure: 0, GCP: 0 };
      }
      datelyData[date].GCP = anomalies;
    });
  }

  const ChartDataSS = Object.entries(datelyData).map(([date, savings]) => {
    const filteredSavings = Object.keys(savings)
      .filter((key) => config.enabledClouds.has(key.toLowerCase()))
      .reduce((obj, key) => {
        obj[key] = savings[key];
        return obj;
      }, {});

    return {
      date: date,
      ...filteredSavings,
    };
  });

  ChartDataSS.sort((a, b) => {
    const aDate = parse(a.date, "MMM, yy", new Date());
    const bDate = parse(b.date, "MMM, yy", new Date());
    return compareAsc(aDate, bDate);
  });

  const maxLength = ChartDataSS?.length || 0;

  // -------------------------------------------------------download csv code---------------------------------------

  const handleDownloadCsv = () => {
    const headers = [
      "date",
      ...Object.keys(ChartDataSS[0]).filter(
        (key) => key !== "date" || key !== "date"
      ),
    ];

    const csvData = [
      headers,
      ...ChartDataSS.map((item) => [
        item.date,
        ...headers.slice(1).map((header) => item[header] || "-"),
      ]),
    ];

    if (csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {((!isLoading && data) || error) && (
          <div className={styles.heading_days}>
            <span className={styles.heading}>All Cloud Cost Anomalies</span>
            <span className={styles.last_days}> Last 90 Days</span>
          </div>
        )}
        {!isLoading && !error && data && (
          <div className={styles.menu_icon_css1}>
            <Dropdown style={{ marginBottom: 2 }}>
              <Dropdown.Toggle
                variant="light"
                id={styles.custom_dropdown_toggle}
                className={styles.custom_dropdown_toggle}
                style={{ background: "transparent", border: "none" }}
              >
                <img src={menuicon} alt="Menu" />
              </Dropdown.Toggle>
              <Dropdown.Menu className={styles.dropdown_menu}>
                <Dropdown.Item
                  className={styles.dropdown_item}
                  eventKey="bar"
                  onClick={() => setChartType("bar")}
                >
                  <img
                    src={barcharticon}
                    alt=""
                    style={{ marginRight: "10px" }}
                  ></img>
                  Bar Chart
                </Dropdown.Item>
                <Dropdown.Item
                  className={styles.dropdown_item}
                  eventKey="line"
                  onClick={() => setChartType("line")}
                >
                  <img
                    src={linecharticon}
                    alt=""
                    style={{ marginRight: "10px" }}
                  ></img>
                  Line Chart
                </Dropdown.Item>
                <Dropdown.Divider />

                <Dropdown.Item
                  className={styles.dropdown_item}
                  eventKey="Download as CSV"
                  onClick={handleDownloadCsv}
                >
                  <img src={download} alt="" style={{ marginRight: "10px" }} />
                  Download as CSV
                </Dropdown.Item>
                <CSVLink
                  data={ChartDataSS}
                  filename={`allClouds-recommendation-data.csv`}
                  target="_blank"
                  ref={csvLinkRef}
                  style={{ display: "none" }}
                >
                  Download
                </CSVLink>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </div>
      {isLoading && <BarGraphErrorStates />}
      {error && (
        <ScenariosErrorState error={error.message} messageConfig={{}} />
      )}
      {((data?.azure && data?.azure.length > 0) ||
        (data?.aws && data?.aws.length > 0) ||
        (data?.gcp && data?.gcp.length > 0)) && (
        <>
          {ChartDataSS && chartType === "bar" ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={ChartDataSS}
                margin={{ top: 10, right: 0, left: -60, bottom: 8 }}
              >
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  stroke="#A3A3A3"
                  fontSize={10}
                  fontWeight={400}
                  fontFamily="Helvetica"
                  // tickFormatter={(value) => format(parseISO(value), "MMM dd")}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  width={120}
                  fontSize={10}
                  fontWeight={400}
                  fontFamily="Helvetica"
                />
                <CartesianGrid strokeDasharray="0" stroke="#F0F0F0" />
                <Tooltip
                  content={(props) => customTooltip(props)}
                  cursor={{ fill: "transparent" }}
                />
                <Legend
                  align="center"
                  verticalAlign="bottom"
                  layout="horizontal"
                  iconSize={12}
                  iconType="Square"
                  fontSize={12}
                  fontWeight={400}
                />

                {Object.keys(ChartDataSS[0] || []).map((key) => {
                  if (key === "date") return null;
                  return (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="a"
                      fill={colorMap[key]}
                      barSize={80}
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={ChartDataSS}
                margin={{ top: 0, right: 3, left: -13, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="0"
                  vertical={false}
                  stroke="#F0F0F0"
                />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  fontSize={10}
                  fontWeight={400}
                  stroke="#A3A3A3"
                />
                <YAxis
                  label={{ fill: "#A3A3A3" }}
                  tickLine={false}
                  axisLine={false}
                  fontSize={10}
                  fontWeight={400}
                  stroke="#A3A3A3"
                />
                <Legend
                  align="center"
                  verticalAlign="bottom"
                  layout="horizontal"
                  iconSize={12}
                  iconType="Square"
                  wrapperStyle={{}}
                  fontSize={12}
                  fontWeight={400}
                  fontStyle="normal"
                />
                <Tooltip
                  content={customTooltip}
                  cursor={{ fill: "transparent" }}
                />
                {Object.keys(ChartDataSS[0] || []).map((key) => {
                  if (key === "date") return null;
                  return (
                    <Line
                      type="straight"
                      dataKey={key}
                      stackId="a"
                      stroke={colorMap[key]}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          )}
        </>
      )}
    </div>
  );
};

export const customTooltip = ({ active, payload, label, tooltipType }) => {
  if (!active || !payload) return null;

  // Calculate total for stacked bar
  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

  return (
    <div className={idleStyles.custom_tooltip}>
      <div className={idleStyles.tooltip_date} style={{ paddingLeft: "8px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div className={idleStyles.tooltip_date_css}>{label}</div>
          <div className={idleStyles.tooltip_date_css}>{total || 0}</div>
        </div>
      </div>

      <div style={{ paddingRight: "12px", width: "100%", paddingLeft: "8px" }}>
        <table>
          <thead>
            <tr>
              <th
                className={idleStyles.cloud_cell}
                style={{ paddingRight: "24px" }}
              >
                Cloud
              </th>
              <th
                className={idleStyles.cost_cell}
                style={{ paddingRight: "24px" }}
              >
                Anomalies
              </th>
            </tr>
          </thead>
          <tbody>
            {payload.map((entry, index) => (
              <tr
                key={`${idleStyles.tooltip_row}-${index}`}
                className={idleStyles.horizontalline_tool}
              >
                <td
                  className={idleStyles.cloud_cell}
                  style={{ paddingRight: "24px" }}
                >
                  {entry?.dataKey?.toLowerCase() === "aws" && (
                    <img
                      src={awsLogo}
                      alt=""
                      className={idleStyles.cloud_logo}
                    />
                  )}
                  {entry?.dataKey?.toLowerCase() === "azure" && (
                    <img
                      src={azureLogo}
                      alt=""
                      className={idleStyles.cloud_logo}
                    />
                  )}
                  {entry?.dataKey?.toLowerCase() === "gcp" && (
                    <img
                      src={gcpLogo}
                      alt=""
                      className={idleStyles.cloud_logo}
                    />
                  )}
                  {entry.dataKey}
                </td>
                <td
                  className={idleStyles.cost_cell}
                  style={{ paddingRight: "24px" }}
                >
                  {entry?.value || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
